/**
 * Notification Settings API Service
 * Handles all API calls for notification settings management
 */

import { API_BASE_URL as RAW_API_BASE_URL } from './api';
import { fetchWithAuthRetry } from './refreshCoordinator';

// Base API configuration
const API_BASE_URL = RAW_API_BASE_URL.replace(/\/$/, '');
const NOTIFICATION_SETTINGS_BASE = `${API_BASE_URL}/notifications/settings`;

// API Response types
export interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Settings types
export interface NotificationSystemSettings {
  id: number;
  ai_enabled: boolean;
  ai_model: string;
  ai_api_endpoint?: string;
  ai_api_timeout: number;
  ai_retry_attempts: number;
  health_alert_confidence: number;
  escalation_confidence: number;
  recommendation_confidence: number;
  sentiment_sensitivity: number;
  aggregation_enabled: boolean;
  aggregation_time_window: number;
  aggregation_message_threshold: number;
  batch_processing_size: number;
  cleanup_interval_hours: number;
  rate_limit_per_minute: number;
  emergency_override_enabled: boolean;
  maintenance_mode: boolean;
  updated_at: string;
  updated_by?: number;
  updated_by_name?: string;
}

export interface HospitalNotificationSettings {
  id: number;
  hospital_id: string;
  hospital_name: string;
  override_system_settings: boolean;
  custom_health_alert_confidence?: number;
  custom_escalation_confidence?: number;
  custom_recommendation_confidence?: number;
  custom_sentiment_sensitivity?: number;
  enable_email_notifications: boolean;
  enable_sms_notifications: boolean;
  enable_push_notifications: boolean;
  disabled_template_ids: string[];
  custom_template_content: Record<string, any>;
  custom_aggregation_time_window?: number;
  custom_aggregation_message_threshold?: number;
  created_at: string;
  updated_at: string;
  updated_by?: number;
  updated_by_name?: string;
  effective_thresholds: {
    health_alert: number;
    escalation: number;
    recommendation: number;
    sentiment: number;
  };
}

export interface AINotificationTemplate {
  id: string;
  template_name: string;
  notification_type: string;
  title_template: string;
  body_template: string;
  preview_template: string;
  target_roles: string[];
  target_departments: string[];
  priority: string;
  requires_ai_analysis: boolean;
  min_confidence_score: number;
  required_variables: string[];
  optional_variables: string[];
  available_actions: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationTemplateSettings {
  id: number;
  template: AINotificationTemplate;
  template_id?: string;
  is_active: boolean;
  override_priority?: string;
  custom_variables: Record<string, any>;
  variable_validation_rules: Record<string, any>;
  delivery_channels: string[];
  delivery_delay_seconds: number;
  target_roles: string[];
  target_departments: string[];
  hospital_overrides: Record<string, any>;
  created_at: string;
  updated_at: string;
  updated_by?: number;
  updated_by_name?: string;
}

export interface NotificationSettingsAudit {
  id: number;
  setting_type: 'system' | 'hospital' | 'template';
  setting_id: number;
  setting_identifier: string;
  field_name: string;
  old_value: string;
  new_value: string;
  changed_by?: number;
  changed_by_name?: string;
  changed_at: string;
  change_reason: string;
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
  request_id?: string;
}

export interface NotificationSettingsSnapshot {
  id: number;
  snapshot_name: string;
  snapshot_type: 'manual' | 'scheduled' | 'pre_change';
  description: string;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  snapshot_size: number;
}

export interface SettingsUpdateRequest {
  updates: Record<string, any>;
  change_reason?: string;
}

export interface SettingsTestRequest {
  notification_type: string;
  template_id?: string;
  hospital_id?: string;
  test_context?: Record<string, any>;
}

export interface SettingsTestResult {
  success: boolean;
  test_results: {
    should_deliver: boolean;
    delivery_channels: string[];
    effective_thresholds: Record<string, number>;
    template_active?: boolean;
    test_context: Record<string, any>;
  };
}

export interface SettingsHealthCheck {
  success: boolean;
  health_status: 'healthy' | 'warning' | 'error';
  data: {
    system_settings_healthy: boolean;
    cache_healthy: boolean;
    database_healthy: boolean;
    total_hospitals: number;
    total_templates: number;
    recent_changes: number;
    last_snapshot?: string;
    issues: string[];
    recommendations: string[];
  };
}

// Sound preferences types
export interface NotificationSoundPreferences {
  id?: number;
  user: number;
  enable_sound_alerts: boolean;
  master_volume: number; // 0.0 to 1.0
  sound_theme: 'default' | 'gentle' | 'professional' | 'minimal';
  
  // Per-notification type settings
  routine_sound: string;
  routine_volume: number;
  urgent_sound: string;
  urgent_volume: number;
  critical_sound: string;
  critical_volume: number;
  ai_sound: string;
  ai_volume: number;
  system_sound: string;
  system_volume: number;
  reply_sound: string;
  reply_volume: number;
  
  // Quiet hours integration
  respect_quiet_hours: boolean;
  emergency_override_quiet_hours: boolean; // Critical sounds even during quiet hours
  
  // Mobile settings
  enable_vibration: boolean;
  vibration_intensity: 'light' | 'medium' | 'strong';
  
  created_at?: string;
  updated_at?: string;
}

export interface SoundTestRequest {
  sound_type: 'routine' | 'urgent' | 'critical' | 'ai' | 'system' | 'reply';
  sound_file?: string;
  volume?: number;
  use_vibration?: boolean;
}

export interface SoundTestResult {
  success: boolean;
  sound_played: string;
  volume_level: number;
  vibration_triggered: boolean;
  browser_supported: boolean;
}

export interface SoundVariation {
  filename: string;
  display_name: string;
  description: string;
  duration: number;
  amplitude: number;
  category: 'routine' | 'urgent' | 'critical' | 'ai' | 'system' | 'reply';
  audio_type: 'bell_tone' | 'soft_chime' | 'chord' | 'marimba_tone' | 'digital';
}

export interface AvailableSounds {
  // Legacy single sounds (backwards compatibility)
  default_sounds: {
    [key: string]: {
      filename: string;
      display_name: string;
      description: string;
      duration: number;
      appropriate_for: string[];
    };
  };
  
  // New comprehensive sound variations
  sound_variations: {
    routine: SoundVariation[];
    urgent: SoundVariation[];
    critical: SoundVariation[];
    ai: SoundVariation[];
    system: SoundVariation[];
    reply: SoundVariation[];
  };
  
  // Sound themes with variations
  themes: {
    [key: string]: {
      name: string;
      description: string;
      sound_mappings: Record<string, string>;
      volume_levels: Record<string, number>;
    };
  };
  
  custom_sounds?: {
    [key: string]: {
      filename: string;
      display_name: string;
      uploaded_by: number;
      created_at: string;
    };
  };
}

// Helper function to make authenticated API calls using session-based auth
const apiCall = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };

  const response = await fetchWithAuthRetry(endpoint, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
};

// System Settings API
export const systemSettingsApi = {
  // Get system settings
  getSettings: (): Promise<NotificationSystemSettings> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/system/`),

  // Update system settings
  updateSettings: (updates: SettingsUpdateRequest): Promise<ApiResponse<NotificationSystemSettings>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/system/`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
};

// Hospital Settings API
export const hospitalSettingsApi = {
  // Get all hospital settings
  getAllSettings: (): Promise<HospitalNotificationSettings[]> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/hospitals/`),

  // Get specific hospital settings
  getSettings: (hospitalId: string): Promise<HospitalNotificationSettings> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/hospitals/${hospitalId}/`),

  // Update hospital settings
  updateSettings: (
    hospitalId: string,
    updates: SettingsUpdateRequest
  ): Promise<ApiResponse<HospitalNotificationSettings>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/hospitals/${hospitalId}/`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
};

// Template Settings API
export const templateSettingsApi = {
  // Get all template settings
  getAllSettings: (): Promise<NotificationTemplateSettings[]> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/templates/`),

  // Get specific template settings
  getSettings: (templateId: string): Promise<NotificationTemplateSettings> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/templates/${templateId}/`),

  // Update template settings
  updateSettings: (
    templateId: string,
    updates: SettingsUpdateRequest
  ): Promise<ApiResponse<NotificationTemplateSettings>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/templates/${templateId}/`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
};

// Audit API
export const auditApi = {
  // Get audit log
  getAuditLog: (params?: {
    setting_type?: string;
    setting_id?: number;
    user_id?: number;
    limit?: number;
  }): Promise<NotificationSettingsAudit[]> => {
    const query = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    return apiCall(`${NOTIFICATION_SETTINGS_BASE}/audit/${query}`);
  },
};

// Snapshot API
export const snapshotApi = {
  // Get all snapshots
  getAllSnapshots: (): Promise<NotificationSettingsSnapshot[]> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/snapshots/`),

  // Get specific snapshot
  getSnapshot: (snapshotId: number): Promise<NotificationSettingsSnapshot> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/snapshots/${snapshotId}/`),

  // Create snapshot
  createSnapshot: (data: {
    snapshot_name: string;
    description?: string;
  }): Promise<ApiResponse<NotificationSettingsSnapshot>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/snapshots/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Restore from snapshot
  restoreSnapshot: (
    snapshotId: number,
    changeReason?: string
  ): Promise<ApiResponse<{ success: boolean; message: string }>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/snapshots/${snapshotId}/`, {
      method: 'PUT',
      body: JSON.stringify({ change_reason: changeReason }),
    }),
};

// Operations API
export const operationsApi = {
  // Test notification settings
  testSettings: (request: SettingsTestRequest): Promise<SettingsTestResult> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/test/`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // Health check
  healthCheck: (): Promise<SettingsHealthCheck> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/health-check/`),

  // Export settings
  exportSettings: (params?: {
    include_system?: boolean;
    include_hospitals?: boolean;
    include_templates?: boolean;
    hospital_ids?: string[];
    template_ids?: string[];
  }): Promise<Blob> => {
    const query = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    return fetchWithAuthRetry(`${NOTIFICATION_SETTINGS_BASE}/export/${query}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(response => {
      if (!response.ok) throw new Error('Export failed');
      return response.blob();
    });
  },

  // Import settings
  importSettings: (data: {
    settings_data: any;
    import_mode?: 'merge' | 'replace' | 'create_only';
    change_reason?: string;
  }): Promise<ApiResponse<{ success: boolean; message: string; snapshot_id: number }>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/import/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Reset settings
  resetSettings: (data: {
    reset_type?: 'system' | 'hospital' | 'template' | 'all';
    change_reason?: string;
  }): Promise<ApiResponse<{ success: boolean; message: string; backup_snapshot_id: number }>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/reset/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Sound Preferences API
export const soundPreferencesApi = {
  // Get current user's sound preferences
  getPreferences: (): Promise<NotificationSoundPreferences> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/sounds/preferences/`),

  // Update current user's sound preferences
  updatePreferences: (
    preferences: Partial<NotificationSoundPreferences>
  ): Promise<ApiResponse<NotificationSoundPreferences>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/sounds/preferences/`, {
      method: 'PUT',
      body: JSON.stringify(preferences),
    }),

  // Get available sound files
  getAvailableSounds: (): Promise<AvailableSounds> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/sounds/available/`),

  // Test a notification sound
  testSound: (request: SoundTestRequest): Promise<SoundTestResult> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/sounds/test/`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // Reset sound preferences to defaults
  resetToDefaults: (): Promise<ApiResponse<NotificationSoundPreferences>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/sounds/preferences/reset/`, {
      method: 'POST',
    }),

  // Get sound theme presets
  getThemePresets: (): Promise<{
    themes: {
      [key: string]: {
        name: string;
        description: string;
        sound_mappings: Record<string, string>;
        volume_levels: Record<string, number>;
      };
    };
  }> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/sounds/themes/`),

  // Apply sound theme preset
  applyTheme: (theme: string): Promise<ApiResponse<NotificationSoundPreferences>> =>
    apiCall(`${NOTIFICATION_SETTINGS_BASE}/sounds/themes/${theme}/apply/`, {
      method: 'POST',
    }),
};

// Main API export
export const notificationSettingsApi = {
  system: systemSettingsApi,
  hospital: hospitalSettingsApi,
  template: templateSettingsApi,
  audit: auditApi,
  snapshot: snapshotApi,
  operations: operationsApi,
  sounds: soundPreferencesApi,
};

export default notificationSettingsApi;